/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject } from '@angular/core';
import { fhirR4 } from '@smile-cdr/fhirts';
import { DataService } from '../services/data.service';
import { ToastService } from '../error/toast/toast.service';
import { FileUploadErrorType } from '../models/FileUploadErrorType';
import { NoChangeError } from '../models/NoChangeError';
import { FileUploadErrorResponse } from '../models/FileUploadErrorResponse';
import partition from 'lodash/partition';
import { Detail } from '../models/Detail';
import { EnvService } from '../services/env.service';

@Injectable({
  providedIn: 'root',
})
export class AttachmentService {
  private readonly dataService = inject(DataService);
  private readonly toastService = inject(ToastService);
  private readonly env = inject(EnvService);


  async processAttachments(
    detail: Detail,
    qrItem: fhirR4.QuestionnaireResponseItem,
    newOrigin: fhirR4.Extension[],
  ): Promise<fhirR4.QuestionnaireResponseAnswer[] | undefined> {
    if (detail.remove) {
      return qrItem.answer?.filter(
        (answer) =>
          answer.valueReference?.reference !== detail.attachment.reference,
      );
    } else {
      return (qrItem.answer ?? []).concat(
        await this.createAttachments(detail.value, newOrigin),
      );
    }
  }

  async createAttachments(
    fileList: FileList,
    newOrigin: fhirR4.Extension[],
  ): Promise<fhirR4.QuestionnaireResponseAnswer[]> {
    const validFiles = this.filterInvalidFiles(fileList);
    const results = await Promise.all(
      validFiles.map(async (file) => {
        try {
          const resource = await this.dataService.createDocumentReference(file);
          const answer = new fhirR4.QuestionnaireResponseAnswer();
          answer.valueReference = {
            display: file?.name,
            type: resource.resourceType,
            reference: resource.resourceType + '/' + resource.id,
          };
          answer.extension = newOrigin;
          return answer;
        } catch (err: any) {
          return this.catchUploadErrors(err, file);
        }
      }),
    );
    return this.processUploadResults(results);
  }

  filterInvalidFiles(fileList: FileList) {
    const [invalidFiles, validFiles] = partition(
      fileList,
      (file) => !file || !this.isValidType(file) || !this.isValidSize(file),
    );
    const [invalidType, invalidSize] = partition(
      invalidFiles,
      (file) => !file || !this.isValidType(file),
    );
    this.displayErrorsByType(invalidSize, FileUploadErrorType.INVALID_SIZE);
    this.displayErrorsByType(invalidType, FileUploadErrorType.INVALID_TYPE);
    return validFiles;
  }

  isValidSize(file: File): boolean {
    const maxSize =
      Number.parseFloat(this.env.environment.maxUploadSize ?? '0') * 1024 * 1024;
    if (maxSize > 0 && file.size > maxSize) {
      return false;
    }
    return true;
  }

  isValidType(file: File): boolean {
    if (
      this.env.environment.attachmentIncludeTypes &&
      this.env.environment.attachmentIncludeTypes?.length > 0
    ) {
      return (this.env.environment.attachmentIncludeTypes as string[]).includes(
        file.type,
      );
    }
    if (
      this.env.environment.attachmentExcludeTypes &&
      this.env.environment.attachmentExcludeTypes?.length > 0
    ) {
      return !(
        this.env.environment.attachmentExcludeTypes as string[]
      ).includes(file.type);
    }
    return true;
  }

  catchUploadErrors(err: any, file: File): FileUploadErrorResponse {
    if ([401, 403].includes(err.statusCode)) {
      return new FileUploadErrorResponse(FileUploadErrorType.NO_ACCESS, file);
    } else if (err.statusCode === 400) {
      return new FileUploadErrorResponse(
        FileUploadErrorType.INVALID_FILE,
        file,
      );
    } else {
      return new FileUploadErrorResponse(FileUploadErrorType.NETWORK, file);
    }
  }

  processUploadResults(
    results: (fhirR4.QuestionnaireResponseAnswer | FileUploadErrorResponse)[],
  ): fhirR4.QuestionnaireResponseAnswer[] {
    const [errors, answers] = partition(
      results,
      (value) => value instanceof FileUploadErrorResponse,
    );
    this.filterErrorsByType(errors, FileUploadErrorType.INVALID_FILE);
    this.filterErrorsByType(errors, FileUploadErrorType.NO_ACCESS);
    this.filterErrorsByType(errors, FileUploadErrorType.NETWORK);
    if (answers.length === 0) throw new NoChangeError();
    return answers;
  }

  filterErrorsByType(
    responses: FileUploadErrorResponse[],
    type: FileUploadErrorType,
  ) {
    const files = responses
      .filter((value) => value.type === type)
      .map((value) => value.file);
    this.displayErrorsByType(files, type);
  }

  displayErrorsByType(files: File[], errorType: FileUploadErrorType) {
    const filenames = files.map(
      (file, index) => file?.name || `File(${index})`,
    );
    if (filenames.length > 0)
      this.toastService.showFileUploadErrorToast(errorType, {
        addlText: filenames.join(', '),
      });
  }
}
